// /hooks/use-evm-balances.ts
import { useState, useEffect, useCallback } from 'react';

interface EVMBalances {
  ethereum: Record<string, number>;
  bsc: Record<string, number>;
  polygon: Record<string, number>;
  tron: Record<string, number>;
}

interface UseEVMBalancesProps {
  userId: string | null;
  refetchInterval?: number;
  enabled?: boolean;
}

export function useEVMBalances({ 
  userId, 
  refetchInterval = 30000, 
  enabled = true 
}: UseEVMBalancesProps) {
  const [balances, setBalances] = useState<EVMBalances>({
    ethereum: {},
    bsc: {},
    polygon: {},
    tron: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const chains = ['ethereum', 'bsc', 'polygon', 'tron'] as const;

  const fetchBalances = useCallback(async (showRefreshing = false) => {
    if (!userId || !enabled) {
      setLoading(false);
      return;
    }

    if (showRefreshing) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    setError(null);

    try {
      const results = await Promise.all(
        chains.map(async (chain) => {
          try {
            const response = await fetch(
              `/api/transfer-evm?action=get-balances&userId=${encodeURIComponent(
                userId
              )}&chain=${chain}`
            );

            if (!response.ok) {
              console.warn(`[EVMBalances] ${chain} returned status ${response.status}`);
              return { chain, balances: {}, success: false };
            }

            const data = await response.json();
            
            if (data.success && data.balances) {
              return { chain, balances: data.balances, success: true };
            } else if (data.warning) {
              console.warn(`[EVMBalances] ${chain} warning:`, data.warning);
              return { chain, balances: {}, success: true };
            } else {
              return { chain, balances: {}, success: false };
            }
          } catch (err) {
            console.error(`[EVMBalances] Error fetching ${chain} balances:`, err);
            return { chain, balances: {}, success: false };
          }
        })
      );

      const newBalances: EVMBalances = {
        ethereum: {},
        bsc: {},
        polygon: {},
        tron: {},
      };

      results.forEach((result) => {
        if (result.success) {
          newBalances[result.chain as keyof EVMBalances] = result.balances;
        }
      });

      setBalances(newBalances);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch EVM balances';
      setError(errorMessage);
      console.error('Error fetching EVM balances:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId, enabled]);

  // Initial fetch
  useEffect(() => {
    if (enabled && userId) {
      fetchBalances();
    }
  }, [enabled, userId, fetchBalances]);

  // Set up refetch interval
  useEffect(() => {
    if (!enabled || !userId || refetchInterval <= 0) return;

    const interval = setInterval(() => {
      fetchBalances(true);
    }, refetchInterval);

    return () => clearInterval(interval);
  }, [enabled, userId, refetchInterval, fetchBalances]);

  // Helper function to get balance for a specific token on a chain
  const getBalance = useCallback(
    (chain: 'ethereum' | 'bsc' | 'polygon' | 'tron', tokenSymbol: string): number => {
      return balances[chain]?.[tokenSymbol] || 0;
    },
    [balances]
  );

  // Helper function to get total USD value for a chain
  const getChainTotalValue = useCallback(
    (chain: 'ethereum' | 'bsc' | 'polygon' | 'tron', prices: Record<string, number>): number => {
      const chainBalances = balances[chain];
      if (!chainBalances) return 0;

      return Object.entries(chainBalances).reduce((total, [symbol, amount]) => {
        const price = prices[symbol] || 0;
        return total + amount * price;
      }, 0);
    },
    [balances]
  );

  return {
    balances,
    loading,
    error,
    refreshing,
    refetch: () => fetchBalances(true),
    getBalance,
    getChainTotalValue,
  };
}
